import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Input,
  Breadcrumb,
  Button,
  message,
  Modal,
  DatePicker,
  Space,
  Grid,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Avatar,
  Tooltip,
  Dropdown,
  Tag,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  DeleteOutlined,
  RightOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
  PhoneOutlined,
  CalendarOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  SwapOutlined,
  HistoryOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { historiqueNumerosService } from '../../common/services';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Can from '../../components/can';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Générer une couleur basée sur le nom
const getAvatarColor = (name) => {
  const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#1890ff', '#722ed1', '#eb2f96'];
  const index = (name || '').charCodeAt(0) % colors.length;
  return colors[index];
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  let local = phone.replace('+225', '').trim();
  return '+225 ' + local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

const formatDate = (date, dateStr) => {
  if (dateStr) return dateStr;
  if (!date) return '-';

  const formats = ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm', 'DD/MM/YYYY'];
  let parsed = dayjs(date);

  if (!parsed.isValid()) {
    for (const fmt of formats) {
      parsed = dayjs(date, fmt);
      if (parsed.isValid()) break;
    }
  }

  if (parsed.isValid()) {
    return parsed.format('DD/MM/YYYY HH:mm');
  }

  return typeof date === 'string' ? date : '-';
};

const HistoriqueNumeros = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // Statistiques
  const [stats, setStats] = useState({
    totalChangements: 0,
    utilisateursUniques: 0,
    today: 0,
    thisMonth: 0,
  });

  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-dateChangement');

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  // Calculer les stats
  const calculateStats = useCallback((data) => {
    const today = dayjs().format('DD/MM/YYYY');
    const thisMonth = dayjs().format('MM/YYYY');

    const todayCount = data.filter(d => {
      const dateStr = d.dateChangementStr || formatDate(d.dateChangement, null);
      return dateStr.startsWith(today);
    }).length;

    const monthCount = data.filter(d => {
      const dateStr = d.dateChangementStr || formatDate(d.dateChangement, null);
      const parts = dateStr.split(' ')[0]?.split('/');
      if (parts && parts.length >= 3) {
        return `${parts[1]}/${parts[2]}` === thisMonth;
      }
      return false;
    }).length;

    const utilisateursUniques = new Set(data.map(d => d.user?._id || d.user?.phone)).size;

    setStats({
      totalChangements: data.length,
      utilisateursUniques,
      today: todayCount,
      thisMonth: monthCount,
    });
  }, []);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      // Supprimer les espaces de la recherche pour les numéros de téléphone
      const cleanedQ = q ? q.replace(/\s+/g, '') : '';
      const params = {
        page, limit, sort,
        q: cleanedQ || undefined,
        start: dateRange?.[0]?.format('YYYY-MM-DD'),
        end: dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      const data = await historiqueNumerosService.getList(params);

      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
        calculateStats(data);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
        // Fetch all for stats
        const allData = await historiqueNumerosService.getList({ ...params, limit: 10000 });
        calculateStats(Array.isArray(allData) ? allData : (allData.items || []));
      }
    } catch (e) {
      console.error('Erreur chargement historique numéros :', e);
      message.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, q, dateRange, calculateStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleDelete = (row) => {
    const { _id, ancienNumero, nouveauNumero, user } = row;
    const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Inconnu';

    Modal.confirm({
      title: (
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          <span>Confirmer la suppression</span>
        </Space>
      ),
      content: (
        <Card size="small" style={{ marginTop: 16, backgroundColor: '#fff2f0' }}>
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <Text type="secondary">Utilisateur :</Text>
              <br />
              <Text strong>{fullName}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Ancien n° :</Text>
              <br />
              <Tag color="red">{formatPhoneNumber(ancienNumero)}</Tag>
            </Col>
            <Col span={12}>
              <Text type="secondary">Nouveau n° :</Text>
              <br />
              <Tag color="green">{formatPhoneNumber(nouveauNumero)}</Tag>
            </Col>
          </Row>
        </Card>
      ),
      okText: "Oui, supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      width: isMobile ? '95%' : 480,
      onOk: async () => {
        try {
          await historiqueNumerosService.supprimerHistoriqueNumerosById(_id);
          message.success("Historique supprimé avec succès !");
          fetchList();
        } catch (error) {
          console.error("Erreur suppression :", error);
          message.error("Une erreur est survenue pendant la suppression.");
        }
      }
    });
  };

  // Export
  const fetchAllForExport = async () => {
    const cleanedQ = q ? q.replace(/\s+/g, '') : '';
    const params = {
      page: 1, limit: 5000, sort,
      q: cleanedQ || undefined,
      start: dateRange?.[0]?.format('YYYY-MM-DD'),
      end: dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    const data = await historiqueNumerosService.getList(params);
    return Array.isArray(data) ? data : (data.items || []);
  };

  const exportExcel = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const rows = all.map(it => ({
        Nom: it?.user?.firstName || '',
        Prénom: it?.user?.lastName || '',
        'Ancien numéro': formatPhoneNumber(it.ancienNumero),
        'Nouveau numéro': formatPhoneNumber(it.nouveauNumero),
        'Date de changement': formatDate(it.dateChangement, it.dateChangementStr),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Historique_numeros');
      XLSX.writeFile(wb, 'historique_numeros.xlsx');
      message.success({ content: 'Export Excel réussi!', key: 'export' });
    } catch (e) {
      message.error({ content: 'Export Excel: une erreur est survenue.', key: 'export' });
    }
  };

  const exportCSV = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const rows = all.map(it => ({
        Nom: it?.user?.firstName || '',
        Prenom: it?.user?.lastName || '',
        AncienNumero: formatPhoneNumber(it.ancienNumero),
        NouveauNumero: formatPhoneNumber(it.nouveauNumero),
        DateChangement: formatDate(it.dateChangement, it.dateChangementStr),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'historique_numeros.csv');
      message.success({ content: 'Export CSV réussi!', key: 'export' });
    } catch (e) {
      message.error({ content: 'Export CSV: une erreur est survenue.', key: 'export' });
    }
  };

  const exportPDF = async () => {
    try {
      message.loading({ content: 'Export en cours...', key: 'export' });
      const all = await fetchAllForExport();
      const doc = new jsPDF();
      doc.text('Historique des changements de numéros', 14, 10);
      autoTable(doc, {
        startY: 20,
        head: [['Nom', 'Prénom', 'Ancien n°', 'Nouveau n°', 'Date']],
        body: all.map(it => [
          it?.user?.firstName || '',
          it?.user?.lastName || '',
          formatPhoneNumber(it.ancienNumero),
          formatPhoneNumber(it.nouveauNumero),
          formatDate(it.dateChangement, it.dateChangementStr)
        ]),
      });
      doc.save('historique_numeros.pdf');
      message.success({ content: 'Export PDF réussi!', key: 'export' });
    } catch (e) {
      message.error({ content: 'Export PDF: une erreur est survenue.', key: 'export' });
    }
  };

  const exportMenuItems = [
    { key: 'excel', icon: <FileExcelOutlined style={{ color: '#52c41a' }} />, label: 'Excel (.xlsx)', onClick: exportExcel },
    { key: 'csv', icon: <DownloadOutlined style={{ color: '#1890ff' }} />, label: 'CSV', onClick: exportCSV },
    { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />, label: 'PDF', onClick: exportPDF },
  ];

  const columns = [
    {
      title: 'Utilisateur',
      key: 'user',
      width: 200,
      render: (_, record) => {
        const firstName = record.user?.firstName || '';
        const lastName = record.user?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim() || 'Inconnu';
        const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';

        return (
          <Space>
            <Avatar style={{ backgroundColor: getAvatarColor(firstName) }} size={40}>
              {initials}
            </Avatar>
            <div>
              <Text strong style={{ display: 'block', fontSize: 13 }}>{fullName}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Ancien numéro',
      dataIndex: 'ancienNumero',
      key: 'ancienNumero',
      width: 160,
      sorter: true,
      render: (v) => (
        <Tag
          icon={<PhoneOutlined />}
          style={{
            backgroundColor: '#fff2f0',
            borderColor: '#ff4d4f',
            color: '#ff4d4f',
          }}
        >
          {formatPhoneNumber(v)}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'arrow',
      width: 50,
      render: () => (
        <ArrowRightOutlined style={{ color: '#52c41a', fontSize: 16 }} />
      ),
    },
    {
      title: 'Nouveau numéro',
      dataIndex: 'nouveauNumero',
      key: 'nouveauNumero',
      width: 160,
      sorter: true,
      render: (v) => (
        <Tag
          icon={<PhoneOutlined />}
          style={{
            backgroundColor: '#f6ffed',
            borderColor: '#52c41a',
            color: '#52c41a',
          }}
        >
          {formatPhoneNumber(v)}
        </Tag>
      ),
    },
    {
      title: 'Date de changement',
      dataIndex: 'dateChangement',
      key: 'dateChangement',
      width: 150,
      sorter: true,
      render: (d, row) => {
        const dateStr = formatDate(d, row.dateChangementStr);
        const parts = dateStr.split(' ');

        return (
          <Tooltip title={dateStr}>
            <Space direction="vertical" size={0}>
              <Text style={{ fontSize: 12 }}>{parts[0] || dateStr}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{parts[1] || ''}</Text>
            </Space>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Can perm="userPhone.history:delete">
          <Tooltip title="Supprimer">
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Can>
      ),
    },
  ];

  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);

    if (sorter?.field || sorter?.columnKey) {
      const key = sorter.columnKey || sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';
      if (['firstName', 'lastName', 'ancienNumero', 'nouveauNumero', 'dateChangement'].includes(key)) {
        setSort(`${dir}${key}`);
      }
    }
  };

  return (
    <>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: 'Historique Modifications' },
          { title: 'Numéros' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* Statistiques */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none'
            }}
          >
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Total changements</span>}
              value={stats.totalChangements}
              valueStyle={{ color: '#fff', fontSize: isMobile ? 20 : 24 }}
              prefix={<SwapOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{ background: '#e6f7ff', borderColor: '#1890ff' }}
          >
            <Statistic
              title={<span style={{ color: '#1890ff' }}>Utilisateurs uniques</span>}
              value={stats.utilisateursUniques}
              valueStyle={{ color: '#1890ff', fontSize: isMobile ? 20 : 24 }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{ background: '#f6ffed', borderColor: '#52c41a' }}
          >
            <Statistic
              title={<span style={{ color: '#52c41a' }}>Aujourd'hui</span>}
              value={stats.today}
              valueStyle={{ color: '#52c41a', fontSize: isMobile ? 20 : 24 }}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            size="small"
            style={{ background: '#fff7e6', borderColor: '#fa8c16' }}
          >
            <Statistic
              title={<span style={{ color: '#fa8c16' }}>Ce mois</span>}
              value={stats.thisMonth}
              valueStyle={{ color: '#fa8c16', fontSize: isMobile ? 20 : 24 }}
              prefix={<HistoryOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filtres et tableau */}
      <Card>
        {/* Filtres */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={10}>
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Rechercher par nom, numéro..."
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              allowClear
              size={isMobile ? 'middle' : 'large'}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              onChange={(r) => { setDateRange(r); setPage(1); }}
              placeholder={['Date début', 'Date fin']}
              format="DD/MM/YYYY"
              allowClear
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
              suffixIcon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Dropdown
              menu={{ items: exportMenuItems }}
              trigger={['click']}
            >
              <Button
                icon={<DownloadOutlined />}
                size={isMobile ? 'middle' : 'large'}
                style={{ width: '100%' }}
              >
                Exporter <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
              </Button>
            </Dropdown>
          </Col>
        </Row>

        {/* Résultats */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary">
            {total} changement{total > 1 ? 's' : ''} trouvé{total > 1 ? 's' : ''}
          </Text>
        </div>

        {/* Tableau */}
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          loading={isLoading}
          onChange={onTableChange}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 900 }}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            pageSizeOptions: [5, 10, 20, 50, 100],
            showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total}`,
          }}
          rowClassName={(_, index) =>
            index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
          }
        />
      </Card>

      {/* Styles */}
      <style>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-dark {
          background-color: #fafafa;
        }
        .table-row-light:hover td,
        .table-row-dark:hover td {
          background-color: #e6f7ff !important;
        }
      `}</style>
    </>
  );
};

export default HistoriqueNumeros;
